import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import UpdateLeadModal from "../Components/EditLeadModalDemo"; // Adjust if named EditLeadModal.tsx
import AddLeadModal from "../Components/AddLeadModalDemo"; // Import the new modal
import {
  fetchAllowedMenus,
  fetchLeadData,
  fetchLeadDataById,
  fetchLocationData,
  createLead,
} from "../Services/APIService";
import OverlayLoader from "../Components/OverlayLoader";

interface Lead {
  leadId: number;
  firstName: string;
  lastName: string;
  gender: string;
  dob: string;
  dom: string;
  mobile: string;
  email: string;
  address: string;
  stateId: number;
  districtId: number;
  cityId: number;
  zipCode: number | string;
  sourceId: number;
  subSourceId: number;
  currentStatus: number;
  incomeFrom: number | string;
  incomeTo: number | string;
  initialAssignedUser: number;
  currentAssignedUser: number;
  budgetMin?: number;
  budgetMax?: number;
  tenantId: number;
  modifiedBy?: number;
  assignType: string;
  initialAssignedUserName: string;
  currentAssignedUserName: string;
  sourceName: string;
  subSourceName: string;
  currentStatusName: string;
  createdOn: string;

  countryCode: string;
  alternateMobile: string;
  alternateMobileCountryCode: string;
}

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface Location {
  locationID: number;
  locationName: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  gender: string;
  dob?: string;
  dom?: string;
  mobile: string;
  email: string;
  address: string;
  stateId: number;
  districtId: number;
  cityId: number;
  zipCode: number; // Change to number
  sourceId: number;
  subSourceId: number;
  currentStatus: number;
  incomeFrom: number; // Change to number
  incomeTo: number; // Change to number
  budgetMin?: number;
  budgetMax?: number;
  initialAssignedUser: number;
  currentAssignedUser: number;
  tenantId: number;
  createdBy: number;
  assignType: string;
  createdOn?: string;
}

const LeadList: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[]>([]);
  const location = useLocation();
  const [selectedRows, setSelectedRows] = useState<Lead[]>([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [editLeadData, setEditLeadData] = useState<Lead | null>(null);
  const [isAddModalOpen, setAddModalOpen] = useState(false); // State for AddLeadModal

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location]);

  const fetchLeads = async (): Promise<void> => {
    try {
      const usersData = await fetchLeadData();
      const formattedData: Lead[] = usersData.map((lead: any) => ({
        ...lead,
        tenantId:
          lead.tenantId ?? parseInt(localStorage.getItem("tenantId") ?? "0"),
        assignType: lead.assignType ?? "assign",
      }));
      setLeads(formattedData);
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetchLocationData();
        if (!response) {
          throw new Error("Failed to fetch location data");
        }
        setLocations(response);
      } catch (error) {
        console.error("Error fetching location data:", error);
      }
    };
    fetchLocations();
  }, []);

  const filteredLeads = leads.filter((lead) => {
    const text = searchText.toLowerCase();
    return (
      lead.firstName?.toLowerCase().includes(text) ||
      lead.lastName?.toLowerCase().includes(text) ||
      lead.email?.toLowerCase().includes(text) ||
      lead.mobile?.toLowerCase().includes(text)
    );
  });

  const handleRowSelected = (state: { selectedRows: Lead[] }) => {
    setSelectedRows(state.selectedRows);
  };

  const handleEdit = async (leadId: number) => {
    try {
      const response = await fetchLeadDataById(leadId);
      const userId = parseInt(localStorage.getItem("UserId") ?? "0");
      const tenantId = parseInt(localStorage.getItem("tenantId") ?? "0");

      if (!tenantId) {
        throw new Error("Tenant ID is missing");
      }

      if (response) {
        setEditLeadData({
          ...response,
          tenantId,
          modifiedBy: userId,
          assignType:
            response.initialAssignedUser === userId ? "self" : "assign",
        });
        setEditModalOpen(true);
      } else {
        throw new Error("No lead data found");
      }
    } catch (error) {
      console.error("Error fetching lead edit data:", error);
      toast.error("Failed to fetch lead data for editing");
    }
  };

  const leadDetailsRedirection = async (leadId: number) => {
    try {
      navigate("/leadDetails", {
        state: { leadId },
      });
    } catch (error) {
      console.error("Error redirecting to lead details:", error);
    }
  };

  const leadDashbordRedirection = async (leadId: number) => {
    try {
      navigate("/LeadDashboard", {
        state: { leadId },
      });
    } catch (error) {
      console.error("Error redirecting to lead details:", error);
    }
  };

  const fetchLeadServices = async (): Promise<void> => {
    try {
      const allLeadServices = await fetchLeadData();
      const formattedData: Lead[] = allLeadServices.map((lead: any) => ({
        ...lead,
        tenantId:
          lead.tenantId ?? parseInt(localStorage.getItem("tenantId") ?? "0"),
        assignType: lead.assignType ?? "assign",
      }));
      setLeads(formattedData);
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    const editLeadId = location.state?.editLeadId;

    if (editLeadId) {
      handleEdit(editLeadId);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchLeadServices();
      navigate(location.pathname, { replace: true });
    }

    if (location.state?.message) {
      toast.success(location.state.message);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const handleAddLead = async () => {
    try {
      toast.success("Lead created successfully");
      fetchLeads();
      setAddModalOpen(false); // Close the modal on success
    } catch (error) {
      console.error("Error creating lead:", error);
      toast.error("An error occurred while creating the lead");
    }
  };

  const formatDatetime = (date: Date | string): string => {
    const dateObj =
      typeof date === "string"
        ? new Date(date.endsWith("Z") ? date : `${date}Z`)
        : date;

    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return "Invalid Date";
    }

    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();

    let hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const hoursStr = String(hours).padStart(2, "0");

    return `${day}-${month}-${year} ${hoursStr}:${minutes} ${ampm}`;
  };

  const formatOnlyDate = (date: Date | string): string => {
    const dateObj =
      typeof date === "string"
        ? new Date(date.endsWith("Z") ? date : `${date}Z`)
        : date;

    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return "Invalid Date";
    }

    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();

    let hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const hoursStr = String(hours).padStart(2, "0");

    return `${day}-${month}-${year}`;
  };

  const columns = [
    {
      name: "Lead Name",
      selector: (row: Lead) => row.firstName + " " + row.lastName,
      sortable: true,
      cell: (row: Lead) => {
        const fullName = `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim();
        const initial = row.firstName.charAt(0).toUpperCase();

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                backgroundColor: "rgb(167 133 144)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "10px",
                fontWeight: "bold",
              }}
            >
              {initial}
            </div>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                //leadDetailsRedirection(row.leadId);
                leadDashbordRedirection(row.leadId);
              }}
              style={{
                textDecoration: "none",
                fontWeight: 500,
                color: "#33475b",
              }}
            >
              {fullName}
            </a>
          </div>
        );
      },
      width: "250px",
    },
    {
      name: "Assign User",
      selector: (row: Lead) => row.currentAssignedUserName,
      sortable: true,
    },

    {
      name: "Source",
      selector: (row: Lead) => row.sourceName,
      sortable: true,
    },
    {
      name: "Sub Source",
      selector: (row: Lead) => row.subSourceName,
      sortable: true,
    },
    {
      name: "Current Status",
      selector: (row: Lead) => row.currentStatusName,
      sortable: true,
    },
    {
      name: "Created On",
      cell: (row: Lead) => (
        <span
          title={formatDatetime(row.createdOn)}
          className="hovered-tabledata"
        >
          {formatOnlyDate(row.createdOn)}
        </span>
      ),
      sortable: false,
    },
    {
      name: "",
      cell: (row: Lead) => (
        <div className="action-cell">
          <button
            className="action-button edit-button"
            title="Edit Lead"
            onClick={() => handleEdit(row.leadId)}
          >
            <i className="bi bi-pencil-square"></i>
          </button>
          <button
            className="action-button more-button"
            title="View Lead Details"
            onClick={() => leadDashbordRedirection(row.leadId)}
          >
            <i className="bi bi-eye-fill"></i>
          </button>
          {/* <button className="action-button more-button">
            <i className="bi bi-three-dots"></i>
          </button> */}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "60px",
    },
  ];

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12 col-lg-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">Leads List</div>
                      <div>
                        <input
                          type="text"
                          placeholder="Search by Lead Name, or Location"
                          className="p-2 w-full datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => setAddModalOpen(true)} // Open the AddLeadModal
                        >
                          + Add Lead
                        </button>
                      </div>
                    </div>
                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredLeads}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No Leads found"
                        selectableRows
                        selectableRowsComponentProps={{
                          className: "custom-checkbox",
                        }}
                        onSelectedRowsChange={handleRowSelected}
                        clearSelectedRows={toggleCleared}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {isEditModalOpen && editLeadData && (
              <UpdateLeadModal
                isOpen={isEditModalOpen}
                onClose={() => {
                  setEditModalOpen(false);
                  setEditLeadData(null);
                }}
                leadsData={editLeadData || null}
                onSubmit={async (updatedCampaign) => {
                  setEditModalOpen(false);
                  setEditLeadData(null);
                  toast.success("Lead updated successfully");
                  await fetchLeads();
                }}
              />
            )}

            {isAddModalOpen && (
              <AddLeadModal
                isOpen={isAddModalOpen}
                onClose={() => setAddModalOpen(false)}
                onSubmit={handleAddLead}
              />
            )}
          </main>
        </div>
        <Footer />
      </div>
      <OverlayLoader
        loading={loading}
        message="Loading Lead Data"
        submessage="This might take a moment..."
      />
    </div>
  );
};

export default LeadList;
